<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { ChevronDown, CircleCheck, CircleX, Loader2, X } from 'lucide-vue-next'
import type { ProgressState, ProgressStep } from '~/utils/promoterProgress'

const props = defineProps<{ state: ProgressState, onClose: () => void }>()

// Older steps fold into one line so the card grows with the task instead of
// scrolling inside itself.
const VISIBLE_STEPS = 8

const now = ref(Date.now())
let timer: ReturnType<typeof setInterval> | undefined
onMounted(() => {
  timer = setInterval(() => { now.value = Date.now() }, 500)
})
onUnmounted(() => clearInterval(timer))

const showAll = ref(false)

function formatDuration(ms: number) {
  const seconds = Math.max(0, Math.round(ms / 1000))
  if (seconds < 60) return `${seconds}s`
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`
}

const elapsed = computed(() => formatDuration((props.state.endedAt ?? now.value) - props.state.startedAt))
const running = computed(() => props.state.steps.filter((step) => step.state === 'running'))
const finishedCount = computed(() => props.state.steps.filter((step) => step.state !== 'running').length)
const hiddenCount = computed(() => showAll.value ? 0 : Math.max(0, props.state.steps.length - VISIBLE_STEPS))
const visibleSteps = computed(() => props.state.steps.slice(hiddenCount.value))
const currentLabel = computed(() => {
  if (props.state.status !== 'running') return ''
  const steps = running.value
  if (!steps.length) return '准备中…'
  return steps.length === 1 ? steps[0]!.label : `${steps.length} 项进行中 · ${steps[steps.length - 1]!.label}`
})

function stepDuration(step: ProgressStep) {
  return formatDuration((step.endedAt ?? now.value) - step.startedAt)
}
</script>

<template lang="pug">
.promoter-progress-card(class="w-max min-w-[20rem] max-w-[min(40rem,calc(100vw-2rem))] rounded-lg border bg-background p-3 text-sm text-foreground shadow-lg" data-testid="promoter-progress-card" :data-status="state.status")
  .flex.items-start.gap-2
    Loader2(v-if="state.status === 'running'" class="mt-0.5 size-4 shrink-0 animate-spin text-muted-foreground")
    CircleCheck(v-else-if="state.status === 'done'" class="mt-0.5 size-4 shrink-0 text-emerald-600")
    CircleX(v-else class="mt-0.5 size-4 shrink-0 text-destructive")
    .min-w-0.flex-1
      .flex.items-baseline.gap-2
        .min-w-0.flex-1.truncate.font-medium {{ state.title }}
        //- Fixed width: the ticking timer must not change the layout.
        span(class="w-12 shrink-0 text-right text-xs tabular-nums text-muted-foreground") {{ elapsed }}
      .truncate.text-xs.text-muted-foreground(v-if="currentLabel") {{ currentLabel }}
      .mt-1.whitespace-pre-line.break-words.text-xs(v-if="state.summary" :class="state.status === 'failed' ? 'text-destructive' : 'text-muted-foreground'") {{ state.summary }}
    button(type="button" class="-mr-1 -mt-1 shrink-0 rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground" aria-label="关闭" @click="onClose")
      X(class="size-3.5")
  ol(v-if="state.steps.length" class="ml-1.5 mt-2 space-y-1 border-l pl-3")
    li(v-if="hiddenCount" class="text-xs text-muted-foreground")
      button(type="button" class="flex items-center gap-1 hover:text-foreground" @click="showAll = true")
        ChevronDown(class="size-3")
        span 更早 {{ hiddenCount }} 步 · 共 {{ finishedCount }}/{{ state.steps.length }} 完成
    li(v-for="step in visibleSteps" :key="step.id + step.startedAt" class="flex items-center gap-1.5 text-xs" :class="step.state === 'running' ? 'text-foreground' : 'text-muted-foreground'")
      Loader2(v-if="step.state === 'running'" class="size-3 shrink-0 animate-spin")
      CircleCheck(v-else-if="step.state === 'done'" class="size-3 shrink-0 text-emerald-600")
      CircleX(v-else class="size-3 shrink-0 text-destructive")
      span.min-w-0.flex-1.truncate(:title="step.label") {{ step.label }}
      span(class="w-10 shrink-0 text-right tabular-nums") {{ stepDuration(step) }}
</template>
